import React, { useState, useEffect, useRef } from 'react';
import { useLogto } from '@logto/react';
import { History, AlertCircle, Eye, Download, Home, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_RESOURCE } from '../logtoConfig';

const TOKEN_TIMEOUT_MS = 15000;
const API_TIMEOUT_MS = 20000;

async function getTokenWithTimeout(getAccessToken, ms = TOKEN_TIMEOUT_MS) {
  return Promise.race([
    getAccessToken(API_RESOURCE),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('获取访问令牌超时，请刷新后重试')), ms);
    }),
  ]);
}

const MyHistory = () => {
  const { isAuthenticated, isLoading: isLogtoLoading, getAccessToken } = useLogto();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingHint, setLoadingHint] = useState('正在准备…');
  const [historyRecords, setHistoryRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (isLogtoLoading) {
      setLoading(true);
      setLoadingHint('正在确认登录状态…');
      return;
    }

    if (!isAuthenticated) {
      setError('请先登录后查看历史记录');
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;
    const controller = new AbortController();

    const init = async () => {
      setLoading(true);
      setError(null);
      setLoadingHint('正在获取访问令牌…');
      try {
        const token = await getTokenWithTimeout(getAccessToken);
        if (cancelled || requestId !== requestIdRef.current) return;

        if (!token) {
          setError('无法获取访问令牌，请重新登录');
          return;
        }

        setAccessToken(token);
        setLoadingHint('正在加载历史记录…');
        const response = await axios.get('/api/history/me', {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 100 },
          timeout: API_TIMEOUT_MS,
          signal: controller.signal,
        });
        if (cancelled || requestId !== requestIdRef.current) return;

        setHistoryRecords(response.data.records || []);
        setTotal(response.data.total || response.data.records?.length || 0);
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) return;
        if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || err.name === 'AbortError') {
          return;
        }
        console.error('加载我的历史失败:', err);
        const isNetwork =
          !err.response ||
          err.code === 'ERR_NETWORK' ||
          err.message === 'Network Error';
        setError(
          isNetwork
            ? '无法连接服务器（可能被临时防护拦截）。请稍后再刷新，或在 1Panel WAF 中解封当前 IP。'
            : '加载失败: ' + (err.response?.data?.error || err.message)
        );
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    init();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // 刻意不依赖 getAccessToken，避免其引用变化引发重复请求
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLogtoLoading]);

  const fetchRecordHtml = async (recordId) => {
    if (!accessToken) {
      throw new Error('认证令牌缺失，请刷新页面后重试');
    }
    const response = await axios.get(`/api/history/me/record/${recordId}/html`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      timeout: API_TIMEOUT_MS,
    });
    return response.data?.generated_html || '';
  };

  const handlePreviewHtml = async (recordId) => {
    try {
      const html = await fetchRecordHtml(recordId);
      if (!html) {
        alert('该记录无HTML内容');
        return;
      }
      setPreviewHtml(html);
      setShowPreview(true);
    } catch (err) {
      console.error('预览HTML失败:', err);
      alert('预览失败: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDownloadHtml = async (recordId) => {
    try {
      const html = await fetchRecordHtml(recordId);
      if (!html) {
        alert('该记录无HTML内容');
        return;
      }
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `generated_${recordId}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('下载HTML失败:', err);
      alert('下载失败: ' + (err.response?.data?.error || err.message));
    }
  };

  const goHome = () => {
    try {
      navigate('/');
      setTimeout(() => {
        if (window.location.pathname === '/history') {
          window.location.assign('/');
        }
      }, 120);
    } catch (_e) {
      window.location.assign('/');
    }
  };

  if (loading || isLogtoLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">加载中...</p>
          <p className="text-gray-400 text-sm mt-2">{loadingHint}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">无法查看历史</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex items-center justify-center space-x-3">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
            >
              刷新重试
            </button>
            <button
              onClick={goHome}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              返回首页
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <History className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">我的历史</h1>
                <p className="text-sm text-gray-500">共 {total} 条生成记录</p>
              </div>
            </div>
            <button
              onClick={goHome}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Home className="w-4 h-4" />
              <span>返回主页</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {historyRecords.length === 0 ? (
            <div className="p-16 text-center">
              <History className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg mb-2">暂无历史记录</p>
              <p className="text-gray-400 text-sm">生成网页后，记录会出现在这里</p>
              <button
                onClick={goHome}
                className="mt-6 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                去生成网页
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      序号
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      网页内容描述
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      版式
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      配色
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      HTML
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作时间
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {historyRecords.map((record, index) => (
                    <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 max-w-md">
                        <div className="line-clamp-3" title={record.prompt || record.description}>
                          {record.prompt || record.description || '无描述'}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {record.layout || '未指定'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {record.colors || '未指定'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {record.has_html ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handlePreviewHtml(record.id)}
                              className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                              title="预览HTML"
                            >
                              <Eye className="w-4 h-4" />
                              <span>预览</span>
                            </button>
                            <button
                              onClick={() => handleDownloadHtml(record.id)}
                              className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                              title="下载HTML"
                            >
                              <Download className="w-4 h-4" />
                              <span>下载</span>
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">无</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.created_at
                          ? new Date(record.created_at).toLocaleString('zh-CN')
                          : '未知'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showPreview && previewHtml && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-5/6 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">HTML预览</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                title="HTML预览"
                srcDoc={previewHtml}
                className="w-full h-full border-0"
                sandbox="allow-same-origin allow-scripts"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyHistory;
